'use client'

import { useState, useCallback } from 'react'
import { Button } from '@/General/Button'
import { HeartIcon } from '@heroicons/react/24/solid'
import { useAddItemToWishlistMutation } from '@/generated/graphql'
import { useStore } from '@/store'
import { toastEmitter } from '@/General/Toast'
import {
  isAuthenticationFailure,
  isAuthorizationFailure,
  extractErrorMessage,
} from '@/errorHandling'

function isLoginRequiredError(error: unknown): boolean {
  if (isAuthenticationFailure(error) || isAuthorizationFailure(error)) {
    return true
  }
  const msg =
    (error as { message?: string; response?: { errors?: { message?: string }[] } })
      ?.message ??
    (error as { response?: { errors?: { message?: string }[] } })?.response
      ?.errors?.[0]?.message ??
    ''
  return String(msg).toLowerCase().includes('logged in')
}
import { TextLink } from '@/General/TextLink'
import { LoginModal } from '@/MainLayout/Header/TopMenu/LoginModal'
import { tracking } from '@/tracking'

export const AddToWishlist = ({ productId, productName, variant = 'button' }: AddToWishlistProps) => {
  const userId = useStore(state => state.user?.id)
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  
  const showSuccessToast = useCallback(() => {
    toastEmitter.emit('toast', {
      title: 'Yay!',
      body: (
        <>
          {productName} has been added to your wishlist.
          <br />
          <TextLink href="/account/wishlist">View wishlist</TextLink>
        </>
      ),
      variant: 'success',
    })
  }, [productName])

  const { mutate: addItemToWishlist } = useAddItemToWishlistMutation({
    onSuccess: () => {
      tracking.track('Added product to wishlist', {
        'Product ID': productId,
        'Product name': productName,
      })
      showSuccessToast()
    },
    onError: (error: unknown) => {
      if (isLoginRequiredError(error)) {
        setIsLoginOpen(true)
      } else {
        toastEmitter.emit('toast', {
          title: extractErrorMessage(error, 'Could not add to wishlist. Please try again.'),
          variant: 'error',
        })
      }
    },
  })

  const handleClick = useCallback(() => {
    if (userId && productId) {
      addItemToWishlist({ productId })
    } else {
      setIsLoginOpen(true)
    }
  }, [userId, productId, addItemToWishlist])

  const handleLoginSuccess = useCallback(() => {
    if (!productId) return
    
    const updatedUserId = useStore.getState().user?.id
    if (!updatedUserId) return
    
    addItemToWishlist({ productId })
  }, [productId, addItemToWishlist])

  const loginModal = (
    <LoginModal 
      isLoginOpen={isLoginOpen} 
      setIsLoginOpen={setIsLoginOpen}
      message="Log in to add items to your wishlist"
      onLoginSuccess={handleLoginSuccess}
    />
  )

  if (variant === 'text') {
    return (
      <>
        {loginModal}
        <span
          className="underline text-vi-red cursor-pointer hover:no-underline text-sm flex items-center gap-1"
          onClick={handleClick}
        >
          <HeartIcon className="w-4 h-4" />
          Add to wishlist
        </span>
      </>
    )
  }

  return (
    <>
      {loginModal}
      <Button
        color="blue"
        className="bg-transparent text-vi-gray w-full text-lg py-4"
        onClick={handleClick}
      >
        <HeartIcon className="w-8 h-8 mr-2" /> Add to wishlist
      </Button>
    </>
  )
}

interface AddToWishlistProps {
  productId?: number
  productName?: string
  variant?: 'button' | 'text'
}